import React from 'react'
import { useLocation } from 'react-router-dom'

import {
  ButtonClean,
  List,
  PriceString,
  Table,
  type TableHeaders,
  Tag,
  useMediaQueryContext,
} from '@fourthwall/components'
import { TimezonedDateString } from '@modules/Timezone/components/TimezonedDateString'
import { useShopTime } from '@modules/Timezone/providers/ShopTimeProvider'
import type { FromLocationState } from '@types'

import type { ChargesHistoryDataItem } from '../../Billing.types'
import { TAB_TITLES, tagsAppearances } from '../../consts'
import { ChargesHistoryListItem } from '../ChargesHistoryListItem/ChargesHistoryListItem'
import { PaymentMethod } from '../PaymentMethod/PaymentMethod'

type ChargesHistoryDataProps = {
  data: ChargesHistoryDataItem[]
  isLoading?: boolean
}

export const getTableHeaders = (
  state: FromLocationState,
  timezoneAbbreviation: string,
): TableHeaders<ChargesHistoryDataItem> => [
  {
    id: 'date',
    label: `Date (${timezoneAbbreviation})`,
    render: ({ date }) => <TimezonedDateString date={date} format="MMM DD, YYYY" />,
    width: 1.4,
  },
  {
    id: 'type',
    label: 'Type',
    render: ({ type }) => {
      if (type.url) {
        return <ButtonClean to={type.url} state={state} label={type.label} />
      }

      return type.label
    },
    width: 1.3,
  },
  {
    id: 'amount',
    label: 'Amount',
    render: ({ amount }) => <PriceString value={amount.value} currency={amount.currency} />,
    width: 1.1,
  },
  {
    id: 'method',
    label: 'Method',
    render: ({ method }) => <PaymentMethod method={method} />,
    width: 1,
  },
  {
    id: 'status',
    label: 'Status',
    render: ({ status }) => <Tag label={status} appearance={tagsAppearances[status]} />,
    width: 1.3,
  },
]

export const ChargesHistoryData = ({ data, isLoading }: ChargesHistoryDataProps) => {
  const location = useLocation()
  const media = useMediaQueryContext()
  const { timezoneAbbreviation } = useShopTime()

  const emptyMessage = "You haven't received any charges yet."

  const locationState = {
    from: { label: TAB_TITLES.charges, location },
  }

  if (!media.tablet) {
    return (
      <List
        data={data}
        isLoading={isLoading}
        emptyComponent={emptyMessage}
        renderItem={(chargesHistoryItem: ChargesHistoryDataItem) => (
          <ChargesHistoryListItem
            locationState={locationState}
            chargesHistoryItem={chargesHistoryItem}
          />
        )}
      />
    )
  }

  return (
    <Table
      headers={getTableHeaders(locationState, timezoneAbbreviation)}
      data={data}
      emptyMessage={emptyMessage}
      isLoading={isLoading}
      minWidth={0}
    />
  )
}
